import React, { useEffect, useMemo, useState } from "react";
import {
  Crosshair, LocateFixed, Loader2, X, CheckCircle2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { enqueuePoi } from "@/lib/poiQueue";
import { useI18n } from "@/context/I18nContext";

// 15-category allow-list — MUST match the UP-3a backend
// `services/user_places.CATEGORIES` tuple. Order is by expected
// real-world frequency for a roadside / mall walker. Labels are
// resolved via `useI18n().t("poi.<id>", fallback)` so the same
// picker reads localized text on any device (L-1).
const CATEGORIES = [
  { id: "washroom",  fallback: "Washroom",   icon: "🚻" },
  { id: "water",     fallback: "Water",      icon: "💧" },
  { id: "food",      fallback: "Food",       icon: "🍔" },
  { id: "parking",   fallback: "Parking",    icon: "🅿️" },
  { id: "atm",       fallback: "ATM",        icon: "🏧" },
  { id: "pharmacy",  fallback: "Pharmacy",   icon: "💊" },
  { id: "charging",  fallback: "Charging",   icon: "🔌" },
  { id: "restaurant",fallback: "Restaurant", icon: "🍽️" },
  { id: "shop",      fallback: "Shop",       icon: "🛍️" },
  { id: "entrance",  fallback: "Entrance",   icon: "⬆️" },
  { id: "exit",      fallback: "Exit",       icon: "⬇️" },
  { id: "lift",      fallback: "Lift",       icon: "🛗" },
  { id: "escalator", fallback: "Escalator",  icon: "🔺" },
  { id: "help_desk", fallback: "Help desk",  icon: "ℹ️" },
  { id: "other",     fallback: "Other",      icon: "📍" },
];

/**
 * PoiTagSheet — Phase 3 · GPS-safe worldwide POI tagging surface.
 *
 * Renders a bottom-sheet at `{lat, lng}` from a long-press event.
 * Users adjust the pin manually if GPS is inaccurate, pick a category,
 * optionally add name / note, and Save. GPS failure never blocks the
 * save; a permanent network failure enqueues the tag offline via
 * `poiQueue.js` and the app's PendingSyncBadge takes over.
 *
 * Props:
 *   position:    { lat, lng }  – required, the crosshair coordinates
 *   onClose:     ()  → void    – dismiss without saving
 *   onSaved:     (place) => void – called with the saved public row
 *   onAdjust:    ({lat,lng}) => void – called when the user re-drags
 *                the crosshair; the parent map keeps the crosshair
 *                overlay in sync.
 */
export default function PoiTagSheet({ position, onClose, onSaved, onAdjust }) {
  const { t } = useI18n();
  const catLabel = (id) => t(`poi.${id}`, CATEGORIES.find(c => c.id === id)?.fallback || id);
  const [cat,   setCat]   = useState("washroom");
  const [name,  setName]  = useState("");
  const [note,  setNote]  = useState("");
  const [busy,  setBusy]  = useState(false);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [gpsErr,  setGpsErr]  = useState(null);
  const [savedState, setSavedState] = useState(null);   // 'server' | 'queued'

  // Attempt a background GPS refresh once when the sheet opens. If it
  // succeeds we DO NOT overwrite the user's crosshair automatically —
  // we only remember it so a "📍 Use my GPS location instead" button
  // becomes tappable.
  const [gps, setGps] = useState(null);
  const tryGps = () => {
    if (!navigator.geolocation) { setGpsErr("Geolocation not supported"); return; }
    setGpsBusy(true); setGpsErr(null);
    navigator.geolocation.getCurrentPosition(
      (p) => { setGps({ lat: p.coords.latitude, lng: p.coords.longitude }); setGpsBusy(false); },
      (e) => { setGpsErr(e?.message || "Location unavailable"); setGpsBusy(false); },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
    );
  };
  useEffect(() => { tryGps(); /* silent first attempt */ }, []); // eslint-disable-line

  const useGpsAsPin = () => {
    if (!gps) return;
    onAdjust?.(gps);
  };

  const save = async () => {
    if (!position || !cat) return;
    setBusy(true);
    const body = {
      lat:       Number(position.lat.toFixed(6)),
      lng:       Number(position.lng.toFixed(6)),
      category:  cat,
      name:      name.trim() || null,
      note:      note.trim() || null,
    };
    try {
      // Attempt live save. On any network / server error we fall
      // through to the offline queue so the tag is never lost.
      const r = await api.post("/places/user", body);
      setSavedState("server");
      onSaved?.(r.data?.place || null);
      toast.success(`${catLabel(cat)} ${t("common.saved", "saved")}.`);
      setTimeout(() => onClose?.(), 700);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error("Please sign in to save this place.");
      } else if (status && status >= 400 && status < 500) {
        toast.error(err?.response?.data?.detail || "Could not save this place.");
      } else {
        // 5xx / network-off / timeout — queue for later drain.
        await enqueuePoi(body);
        setSavedState("queued");
        toast.message(
          `${catLabel(cat)} ${t("common.saved_offline", "saved offline — will sync when you're online")}.`,
        );
        setTimeout(() => onClose?.(), 900);
      }
    } finally { setBusy(false); }
  };

  const coord = useMemo(() => (
    position ? `${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}` : "—"
  ), [position]);

  if (!position) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 md:inset-x-auto md:right-6 md:bottom-6 md:w-[380px]"
      style={{
        zIndex: "var(--z-sheet)",
        paddingBottom: "var(--f2n-safe-bottom)",
        paddingInlineStart: "var(--f2n-safe-left)",
        paddingInlineEnd: "var(--f2n-safe-right)",
      }}
      data-testid="poi-tag-sheet"
    >
      <div className="rounded-t-3xl md:rounded-3xl bg-white shadow-2xl border-t md:border border-stone-200 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-2">
            <Crosshair size={16} className="text-emerald-600" />
            <div>
              <div className="text-[13px] font-black text-stone-900">Tag a place here</div>
              <div className="text-[10px] text-stone-500">Long-press or drag to adjust · {coord}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="poi-tag-close"
                  className="p-1 rounded-full hover:bg-stone-100 text-stone-500">
            <X size={16} />
          </button>
        </div>

        {/* GPS row — reveal how the automatic GPS attempt went. Never
             blocks the flow; the user can save without ever using GPS. */}
        <div className="px-4 pb-2">
          {gpsBusy ? (
            <div className="text-[11px] text-stone-500 inline-flex items-center gap-1">
              <Loader2 size={11} className="animate-spin" /> Trying GPS…
            </div>
          ) : gps ? (
            <button onClick={useGpsAsPin}
                    className="text-[11px] font-bold text-emerald-700 inline-flex items-center gap-1 hover:underline"
                    data-testid="poi-tag-use-gps">
              <LocateFixed size={11}/> Use my GPS location ({gps.lat.toFixed(4)}, {gps.lng.toFixed(4)})
            </button>
          ) : (
            <div className="text-[11px] text-amber-700 inline-flex items-center gap-2" data-testid="poi-tag-gps-error">
              GPS unavailable — adjust the pin manually or
              <button onClick={tryGps} className="underline inline-flex items-center gap-1">
                <RefreshCw size={10}/> Retry
              </button>
              {gpsErr ? <span className="text-stone-500">· {gpsErr}</span> : null}
            </div>
          )}
        </div>

        {/* Category picker */}
        <div className="px-4 pb-2">
          <div className="text-[10px] font-black uppercase tracking-wider text-stone-500 mb-1">Category</div>
          <div className="grid grid-cols-3 gap-1.5">
            {CATEGORIES.map(c => (
              <button key={c.id}
                onClick={() => setCat(c.id)}
                className={`rounded-xl border text-[11px] font-black py-2 px-1 flex flex-col items-center gap-0.5
                            ${cat === c.id ? "bg-emerald-600 text-white border-emerald-700"
                                          : "bg-stone-50 text-stone-700 border-stone-200 hover:bg-stone-100"}`}
                data-testid={`poi-tag-cat-${c.id}`}>
                <span aria-hidden>{c.icon}</span>
                <span>{catLabel(c.id)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Optional name + note */}
        <div className="px-4 pb-2 space-y-2">
          <Input value={name} onChange={e => setName(e.target.value.slice(0, 80))}
                 placeholder="Name (optional)" className="h-9 text-sm"
                 data-testid="poi-tag-name"/>
          <textarea value={note} onChange={e => setNote(e.target.value.slice(0, 300))}
                    placeholder="Note (optional)"
                    rows={2}
                    className="w-full rounded-lg border border-stone-200 px-3 py-1.5 text-sm resize-none"
                    data-testid="poi-tag-note"/>
        </div>

        {/* Save + status */}
        <div className="px-4 py-3 border-t border-stone-100 bg-stone-50 flex items-center justify-between">
          {savedState === "server" ? (
            <span className="text-[12px] font-bold text-emerald-700 inline-flex items-center gap-1">
              <CheckCircle2 size={13}/> Saved
            </span>
          ) : savedState === "queued" ? (
            <span className="text-[12px] font-bold text-amber-700 inline-flex items-center gap-1">
              Saved offline — will sync
            </span>
          ) : (
            <span className="text-[10px] text-stone-400">Pin will be visible immediately.</span>
          )}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} className="h-8 text-[11px] font-black">Cancel</Button>
            <Button onClick={save} disabled={busy}
                    className="h-8 text-[11px] font-black bg-emerald-600 hover:bg-emerald-700 text-white px-4"
                    data-testid="poi-tag-save">
              {busy ? <Loader2 size={12} className="animate-spin"/> : "Save place"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
